import type { ICollection } from './index'
import type { CollectionName } from './rows'
import { COLLECTIONS } from './rows'

// Reached through Capacitor's global registry, never imported — Rollup would
// otherwise inline the plugin's web loader into bundle.js. Only the methods
// the engine actually calls are typed; the rest of the plugin stays unseen.

interface IPlugin {
    addUpgradeStatement(options: {
        database: string
        upgrade: { toVersion: number; statements: string[] }[]
    }): Promise<void>
    createConnection(options: {
        database: string
        version: number
        encrypted: boolean
        mode: string
        readonly: boolean
    }): Promise<void>
    checkConnectionsConsistency(options: {
        dbNames: string[]
        openModes: string[]
    }): Promise<{ result?: boolean }>
    open(options: { database: string }): Promise<void>
    run(options: { database: string; statement: string; values: unknown[] }): Promise<unknown>
    query(options: {
        database: string
        statement: string
        values: unknown[]
    }): Promise<{ values?: unknown[] }>
    executeSet(options: {
        database: string
        set: { statement: string; values: unknown[] }[]
        transaction: boolean
    }): Promise<unknown>
}

const capacitor = () => (window as any).Capacitor
const plugin = (): IPlugin | undefined => capacitor()?.Plugins?.CapacitorSQLite

const DATABASE = 'emrouz'
const VERSION = 4

// Collection names the app uses → SQL table names. A free-form name is never
// interpolated into a statement; only the tables listed here exist.
const TABLES = {
    'tasks': 'tasks',
    'events': 'events',
    'custom-occasions': 'custom_occasions',
    'notes': 'notes',
    'occasion-overrides': 'occasion_overrides',
    'day-colors': 'day_colors',
} as const

// One entry per schema version. A fresh database runs them all in order; one
// already at version 1 runs only what comes after it.
const UPGRADES = [
    {
        toVersion: 1,
        statements: [
            'CREATE TABLE IF NOT EXISTS tasks (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);',
            'CREATE TABLE IF NOT EXISTS events (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);',
            'CREATE TABLE IF NOT EXISTS custom_occasions (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);',
            'CREATE TABLE IF NOT EXISTS meta (key TEXT PRIMARY KEY NOT NULL, value TEXT NOT NULL);',
        ],
    },
    {
        toVersion: 2,
        statements: [
            'CREATE TABLE IF NOT EXISTS notes (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);',
        ],
    },
    {
        toVersion: 3,
        statements: [
            'CREATE TABLE IF NOT EXISTS occasion_overrides (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);',
        ],
    },
    {
        toVersion: 4,
        statements: [
            'CREATE TABLE IF NOT EXISTS day_colors (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);',
        ],
    },
]

function tableOf(name: string): string {
    const table = TABLES[name as keyof typeof TABLES]
    if (!table) throw new Error(`storage: unknown collection ${name}`)
    return table
}

function requirePlugin(): IPlugin {
    const sqlite = plugin()
    if (!sqlite) throw new Error('storage: CapacitorSQLite is not available')
    return sqlite
}

type Row = Record<string, unknown>

// iOS answers a non-empty query with the column-name list as an extra first
// row. The plugin's own SQLiteDBConnection wrapper strips that; this engine
// talks to the raw plugin, so every read comes through here instead. Without
// it the flag and the row counts below read the header and never see the data.
function rowsOf(result: { values?: unknown[] }): Row[] {
    const rows = (result.values || []).filter((row): row is Row => !!row && typeof row === 'object')
    return rows[0] && 'ios_columns' in rows[0] ? rows.slice(1) : rows
}

// Opens the on-device file and applies version 1. The caller switches the
// engine only after this resolves; a throw leaves the app on localStorage.
export async function openSqlite() {
    const sqlite = requirePlugin()
    await sqlite.addUpgradeStatement({
        database: DATABASE,
        upgrade: UPGRADES,
    })
    // A WebView reload drops the JS connection map but leaves the native
    // connection. Creating again throws "already exists" and would fall
    // back to localStorage. Consistency true means just open; false means
    // the plugin closed the stale ones and we create a fresh connection.
    const { result } = await sqlite.checkConnectionsConsistency({
        dbNames: [DATABASE],
        openModes: ['RW'],
    })
    if (!result) {
        await sqlite.createConnection({
            database: DATABASE,
            version: VERSION,
            encrypted: false,
            mode: 'no-encryption',
            readonly: false,
        })
    }
    await sqlite.open({ database: DATABASE })
}

export const sqlite = <T extends { id: string }>(name: string): ICollection<T> => {
    const table = tableOf(name)

    return {
        async load() {
            const result = await requirePlugin().query({
                database: DATABASE,
                statement: `SELECT data FROM ${table} ORDER BY rowid`,
                values: [],
            })
            const rows: T[] = []
            for (const row of rowsOf(result)) {
                if (typeof row.data !== 'string') continue
                try {
                    const parsed = JSON.parse(row.data)
                    if (parsed && typeof parsed.id === 'string') rows.push(parsed)
                } catch {
                    // A corrupt row must not block the rest of the collection.
                }
            }
            return rows
        },

        async put(row) {
            // UPSERT, not INSERT OR REPLACE: replace deletes and reinserts,
            // which reassigns rowid and would send a ticked task to the end
            // of the list the next time it is loaded.
            await requirePlugin().run({
                database: DATABASE,
                statement:
                    `INSERT INTO ${table} (id, data) VALUES (?, ?) ` +
                    `ON CONFLICT(id) DO UPDATE SET data = excluded.data`,
                values: [row.id, JSON.stringify(row)],
            })
        },

        async remove(id) {
            await requirePlugin().run({
                database: DATABASE,
                statement: `DELETE FROM ${table} WHERE id = ?`,
                values: [id],
            })
        },
    }
}

async function imported(sqlite: IPlugin): Promise<boolean> {
    const result = await sqlite.query({
        database: DATABASE,
        statement: 'SELECT value FROM meta WHERE key = ?',
        values: ['imported'],
    })
    return rowsOf(result)[0]?.value === '1'
}

async function countIn(sqlite: IPlugin, table: string): Promise<number> {
    const result = await sqlite.query({
        database: DATABASE,
        statement: `SELECT COUNT(*) AS n FROM ${table}`,
        values: [],
    })
    const n = Number(rowsOf(result)[0]?.n)
    return Number.isFinite(n) ? n : 0
}

// One transaction: skip any table that already holds rows (so a lost flag
// cannot duplicate), insert the rest, then write the flag. The localStorage
// copy is left alone.
export async function importIntoSqlite(
    prepared: Record<CollectionName, { id: string }[]>,
) {
    const sqlite = requirePlugin()
    if (await imported(sqlite)) return
    const set: { statement: string; values: unknown[] }[] = []
    for (const name of COLLECTIONS) {
        const table = TABLES[name]
        if (await countIn(sqlite, table) > 0) continue
        for (const row of prepared[name]) {
            set.push({
                statement:
                    `INSERT INTO ${table} (id, data) VALUES (?, ?) ` +
                    `ON CONFLICT(id) DO UPDATE SET data = excluded.data`,
                values: [row.id, JSON.stringify(row)],
            })
        }
    }
    set.push({
        statement:
            `INSERT INTO meta (key, value) VALUES (?, ?) ` +
            `ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
        values: ['imported', '1'],
    })
    await sqlite.executeSet({ database: DATABASE, set, transaction: true })
}
